import React, { useState, useEffect } from 'react';

// bootstrap
import { Form, Button } from 'react-bootstrap';

// custom components 
import { CustomSpinner } from '_atoms';
import { AssistanceSelector } from "_molecules";
import BookingNotesSuggestions, { normalizeBookingNotes } from './booking-notes-suggestions';

// functions
import { getAssistanceArray, canAmendAltPickupTime, hasPresentedAtReception } from '_utils/functions';
import { amendAltPickupTime } from '_services/timeline-service';

// styles 
import { Colors } from '_styles';
import * as TaskColors from '_scenes/portal/timeline-v2/utils/colors';

// redux
import { useSelector, useDispatch } from 'react-redux';
import { 
    updateALTPickupTime,
    selectUpdateALTPickupStatus,
    selectUpdateALTPickupError,
    resetUpdateALTPickup,
    selectTimelineBookingsById
} from '_redux/slices/timeline/timeline-bookings';
import { selectPickUpLocations, selectDropOffLocations } from "_redux/slices/timeline/timeline-areas";
import { selectUsedTheme } from '_redux/slices/timeline/theme';
import { selectFirebaseUser, selectFirebaseInfo } from '_redux/slices/main-screen/user';
import { setBookingLocation } from "_redux/slices/timeline/timeline-data";

// date 
import moment from 'moment';

// drop down menu
import Select from 'react-select';

/**
 * Component for editing ALT pickup and Gate Relocation task details
 * Handles pickup time, pickup location, drop-off location, and relocation reason
 * 
 * @param {Object} props
 * @param {Object} props.job - The ALT pickup or Gate Relocation task to edit
 * @param {Function} props.onSuccess - Callback when update succeeds
 * @param {Function} props.onError - Callback when update fails (optional)
 */
const EditAltPickupDetails = ({ job, onSuccess, onError }) => {

    const dispatch = useDispatch();

    // Redux selectors
    const pickUpLocations = useSelector(selectPickUpLocations);
    const dropOffLocations = useSelector(selectDropOffLocations);
    const usedTheme = useSelector(selectUsedTheme);
    const updateStatus = useSelector(selectUpdateALTPickupStatus);
    const updateError = useSelector(selectUpdateALTPickupError);
    const firebaseUser = useSelector(selectFirebaseUser);
    const dbUser = useSelector(selectFirebaseInfo);
    const bookingsById = useSelector(selectTimelineBookingsById);

    // Determine task type
    const isGateRelocation = job?.isGateRelocation === true;
    const taskTypeLabel = isGateRelocation ? 'Gate Relocation' : 'ALT Pickup';

    // Local state
    const [pickupTime, setPickupTime] = useState(null);
    const [pickupLocation, setPickupLocation] = useState(null);
    const [dropOffLocation, setDropOffLocation] = useState(null);
    const [assistance, setAssistance] = useState([]);
    const [notes, setNotes] = useState("");
    const [relocationReason, setRelocationReason] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [isInitialized, setIsInitialized] = useState(false);
    const [timeReason, setTimeReason] = useState("");
    const [isSavingTime, setIsSavingTime] = useState(false);
    const [initialPickupTime, setInitialPickupTime] = useState(null);

    // scheduled pickup time rules: ADMIN/GARETH only, and a reason when the pax never presented at a Reception Desk
    const canAmendTime = canAmendAltPickupTime(dbUser);
    const presented = hasPresentedAtReception(bookingsById[job?.originalALTPaxId]);
    const isCollected = !!(job?.startTime || job?.active || job?.completed);
    // the picker has minute precision while the app stores seconds, so compare whole minutes
    const isTimeChanged = !!pickupTime && !isNaN(pickupTime.getTime())
        && Math.floor(pickupTime.getTime() / 60000) !== (initialPickupTime ? Math.floor(initialPickupTime.getTime() / 60000) : null);
    const isReasonRequired = isTimeChanged && presented !== true;

    // Reset on mount
    useEffect(() => {
        dispatch(resetUpdateALTPickup());
    }, []);

    // Initialize form values from job
    useEffect(() => {
        if (job && !isInitialized) {
            // Initialize pickup time
            if (job.pickUpTime) {
                let pickupDate = null;
                
                // Handle Firebase timestamp
                if (job.pickUpTime?.seconds) {
                    pickupDate = new Date(job.pickUpTime.seconds * 1000);
                } 
                // Handle time-only string like "08:00" - combine with flight date or today
                else if (typeof job.pickUpTime === 'string' && /^\d{2}:\d{2}$/.test(job.pickUpTime)) {
                    const [hours, minutes] = job.pickUpTime.split(':');
                    const baseDate = job.flightInformation?.flightDate 
                        ? new Date(job.flightInformation.flightDate) 
                        : new Date();
                    baseDate.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
                    pickupDate = baseDate;
                }
                // Handle ISO string or other date formats
                else {
                    pickupDate = new Date(job.pickUpTime);
                }
                
                // Only set if valid date
                if (pickupDate && !isNaN(pickupDate.getTime())) {
                    setPickupTime(pickupDate);
                    setInitialPickupTime(pickupDate);
                }
            }
            
            // Initialize pickup location
            if (job.pickUpLocation?.id) {
                setPickupLocation({
                    value: job.pickUpLocation.id,
                    label: job.pickUpLocation.name
                });
            }
            
            // Initialize drop off location
            if (job.dropOffLocation?.id) {
                setDropOffLocation({
                    value: job.dropOffLocation.id,
                    label: job.dropOffLocation.name
                });
            }

            // Initialize assistance (from passengerInformation.assistance)
            const assistanceData = job.passengerInformation?.assistance || [];
            setAssistance(getAssistanceArray(assistanceData));

            // Initialize notes (from taskNotes array)
            if (Array.isArray(job.taskNotes) && job.taskNotes.length > 0) {
                setNotes(job.taskNotes);
            } else if (job.notes) {
                setNotes(job.notes);
            }

            // Initialize relocation reason (for gate relocations)
            if (job.relocationReason) {
                setRelocationReason(job.relocationReason);
            }

            setIsInitialized(true);
        }
    }, [job, isInitialized]);

    // Monitor update status
    useEffect(() => {
        if (updateStatus === 'succeeded') {
            dispatch(resetUpdateALTPickup());
            onSuccess?.();
        }
        if (updateStatus === 'failed') {
            const errorMsg = `Error updating ${taskTypeLabel}, please contact admin support.`;
            setErrorMessage(errorMsg);
            onError?.(errorMsg);
        }
    }, [updateStatus, taskTypeLabel]);

    // Handle update
    const handleUpdate = async () => {
        setErrorMessage("");
        
        if (!job?._id) {
            setErrorMessage("Invalid task.");
            return;
        }

        // Validate pickup time is a valid Date (only users who can set it are asked for one)
        if (canAmendTime && (!pickupTime || !(pickupTime instanceof Date) || isNaN(pickupTime.getTime()))) {
            setErrorMessage("Please select a valid pickup time.");
            return;
        }

        if (isTimeChanged) {
            if (!canAmendTime) {
                setErrorMessage("Only Team Leaders and Controllers can change the pickup time.");
                return;
            }
            if (isCollected) {
                setErrorMessage("The passenger has already been collected, the pickup time can no longer be changed.");
                return;
            }
            if (!job.originalALTPaxId) {
                setErrorMessage("No passenger booking is linked to this ALT pickup, the pickup time can't be changed.");
                return;
            }
            // no reason = no action: nothing is saved, not even the other fields
            if (isReasonRequired && !timeReason.trim()) {
                setErrorMessage("The passenger has not presented at a Reception Desk. Enter a reason to change the pickup time.");
                return;
            }
        }

        // Build the changes object to track what was modified (the pickup time is logged by the backend)
        const changes = {};
        
        // Check what fields have changed
        if (pickupLocation?.value !== job.pickUpLocation?.id) {
            changes.pickUpLocation = { from: job.pickUpLocation?.name, to: pickupLocation?.label };
        }
        if (dropOffLocation?.value !== job.dropOffLocation?.id) {
            changes.dropOffLocation = { from: job.dropOffLocation?.name, to: dropOffLocation?.label };
        }
        const originalAssistance = job.passengerInformation?.assistance || [];
        if (JSON.stringify(assistance.sort()) !== JSON.stringify([...originalAssistance].sort())) {
            changes.assistance = { from: originalAssistance, to: assistance };
        }
        const originalNotes = Array.isArray(job.taskNotes) && job.taskNotes.length > 0
            ? job.taskNotes
            : (job.notes || "");
        if (JSON.stringify(normalizeBookingNotes(notes)) !== JSON.stringify(normalizeBookingNotes(originalNotes))) {
            changes.notes = { from: originalNotes, to: notes };
        }
        
        // Check relocation reason changes (for gate relocations)
        if (isGateRelocation && relocationReason !== (job.relocationReason || "")) {
            changes.relocationReason = { from: job.relocationReason || "", to: relocationReason };
        }

        // the pickup time goes first through its own endpoint (role + reason checked again there,
        // audit entry on the passenger's history); if it is refused nothing else is saved
        let timeHistoryEntry = null;
        if (isTimeChanged) {
            setIsSavingTime(true);
            try {
                const result = await amendAltPickupTime({
                    taskId: job._id,
                    bookingId: job.originalALTPaxId,
                    pickUpTime: pickupTime.toISOString(),
                    reason: timeReason.trim(),
                });
                timeHistoryEntry = result.editHistoryEntry;
            } catch (e) {
                setErrorMessage(e.message);
                return;
            } finally {
                setIsSavingTime(false);
            }
        }

        if (!Object.keys(changes).length) {
            onSuccess?.();
            return;
        }

        // Create edit history entry
        const editHistoryEntry = {
            editedAt: new Date().toISOString(),
            userId: firebaseUser?.uid || firebaseUser?.id || "unknown",
            userName: firebaseUser?.displayName || firebaseUser?.name || firebaseUser?.email || "Unknown",
            changes: changes,
        };

        // Get existing edit history or create new array
        const existingHistory = Array.isArray(job.editHistory) ? job.editHistory : [];

        dispatch(updateALTPickupTime({
            id: job._id,
            pickUpLocation: pickupLocation?.value ? {
                id: pickupLocation.value,
                name: pickupLocation.label,
            } : null,
            dropOffLocation: dropOffLocation?.value ? {
                id: dropOffLocation.value,
                name: dropOffLocation.label,
            } : null,
            passengerInformation: {
                ...(job.passengerInformation || {}),
                assistance: assistance,
            },
            notes: notes,
            // the whole array is written back, so keep the entry the pickup time endpoint just added
            editHistory: [...existingHistory, ...(timeHistoryEntry ? [timeHistoryEntry] : []), editHistoryEntry],
            // Include relocation reason for gate relocations
            ...(isGateRelocation && { relocationReason: relocationReason }),
        }));

        // If pickup location changed, update the ALT pickup task's paxPositionInfo
        if (changes.pickUpLocation && pickupLocation?.value) {
            const paxLocation = {
                bookingId: job._id, // Use ALT pickup task ID, not original passenger ID
                locationTitle: pickupLocation.label,
                location: pickupLocation.value,
                activity: job?.paxPositionInfo?.activity || "Checked-in",
                time: new Date()
            };
            dispatch(setBookingLocation(paxLocation));
        }
    };

    // Common select styles
    const selectStyles = {
        control: (base) => ({
            ...base,
            maxWidth: "300px",
            borderRadius: "5px",
            backgroundColor: usedTheme?.BACKGROUND_COLOR || '#fff',
            borderColor: usedTheme?.BORDER_COLOR || '#ccc',
            color: usedTheme?.TEXT_COLOR || '#333',
        }),
        placeholder: (provided) => ({
            ...provided,
            color: usedTheme?.TEXT_COLOR || '#333',
            opacity: 0.6,
        }),
        singleValue: (provided) => ({
            ...provided,
            color: usedTheme?.TEXT_COLOR || '#333',
        }),
        menu: (provided) => ({
            ...provided,
            backgroundColor: usedTheme?.BACKGROUND_COLOR || '#fff',
            borderColor: usedTheme?.BORDER_COLOR || '#ccc',
            color: usedTheme?.TEXT_COLOR || '#333',
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isFocused
                ? usedTheme?.BORDER_COLOR || '#eee'
                : usedTheme?.BACKGROUND_COLOR || '#fff',
            color: usedTheme?.TEXT_COLOR || '#333',
            cursor: 'pointer',
        }),
    };

    if (!job?.isALTPickUp && !job?.isGateRelocation) {
        return null;
    }

    return (
        <div style={{ 
            padding: "12px", 
            border: `1px solid ${usedTheme?.BORDER_COLOR || '#ccc'}`, 
            borderRadius: 8, 
            backgroundColor: usedTheme?.BACKGROUND_COLOR || '#fff' 
        }}>
            <Form.Label style={{ color: isGateRelocation ? TaskColors.gateRelocationTask : TaskColors.ALTPickUps, fontWeight: 'bold', marginBottom: '12px' }}>
                {taskTypeLabel} Settings
            </Form.Label>
                        
            {/* Relocation Reason (Gate Relocations only) */}
            {isGateRelocation && (
                <div className="mb-3">
                    <Form.Label style={{ color: usedTheme?.TEXT_COLOR || '#333', fontSize: '13px' }}>Relocation Reason</Form.Label>
                    <Form.Control
                        type="text"
                        value={relocationReason}
                        onChange={(e) => setRelocationReason(e.target.value)}
                        placeholder="e.g., Gate Change, Equipment Issue..."
                        style={{ 
                            backgroundColor: usedTheme?.BACKGROUND_COLOR || '#fff', 
                            borderColor: usedTheme?.BORDER_COLOR || '#ccc', 
                            color: usedTheme?.TEXT_COLOR || '#333',
                            maxWidth: '300px'
                        }}
                    />
                    {job.relocationReason && (
                        <p style={{ fontSize: '11px', color: usedTheme?.TEXT_COLOR || '#333', opacity: 0.5, marginTop: '4px', marginBottom: 0 }}>
                            Current: {job.relocationReason}
                        </p>
                    )}
                </div>
            )}

            {/* Pickup Time */}
            <div className="mb-3">
                <Form.Label style={{ color: usedTheme?.TEXT_COLOR || '#333', fontSize: '13px' }}>Pickup Time</Form.Label>
                <Form.Control
                    type="datetime-local"
                    value={pickupTime ? moment(pickupTime).format('YYYY-MM-DDTHH:mm') : ''}
                    onChange={(e) => setPickupTime(e.target.value ? new Date(e.target.value) : null)}
                    disabled={!canAmendTime || isCollected}
                    style={{ 
                        backgroundColor: usedTheme?.BACKGROUND_COLOR || '#fff', 
                        borderColor: usedTheme?.BORDER_COLOR || '#ccc', 
                        color: usedTheme?.TEXT_COLOR || '#333',
                        maxWidth: '250px'
                    }}
                />
                {job.pickUpTime && (
                    <p style={{ fontSize: '11px', color: usedTheme?.TEXT_COLOR || '#333', opacity: 0.5, marginTop: '4px', marginBottom: 0 }}>
                        Current: {moment(job.pickUpTime?.seconds ? new Date(job.pickUpTime.seconds * 1000) : job.pickUpTime).format('DD/MM/YYYY HH:mm')}
                    </p>
                )}
                <p style={{ fontSize: '11px', color: usedTheme?.TEXT_COLOR || '#333', marginTop: '4px', marginBottom: 0 }}>
                    Presented at Reception Desk: <strong>{presented === true ? 'Yes' : presented === false ? 'No' : 'Unknown'}</strong>
                </p>
                {!canAmendTime && (
                    <p style={{ fontSize: '11px', color: Colors.PRM_LIGHT_BLUE, marginTop: '4px', marginBottom: 0, fontStyle: 'italic' }}>
                        Only Team Leaders and Controllers can change the pickup time.
                    </p>
                )}
                {canAmendTime && isCollected && (
                    <p style={{ fontSize: '11px', color: Colors.PRM_LIGHT_BLUE, marginTop: '4px', marginBottom: 0, fontStyle: 'italic' }}>
                        Passenger already collected.
                    </p>
                )}
            </div>

            {/* Reason for the pickup time change: mandatory when the pax has not presented at a Reception Desk */}
            {isTimeChanged && (
                <div className="mb-3">
                    <Form.Label style={{ color: usedTheme?.TEXT_COLOR || '#333', fontSize: '13px' }}>
                        Reason for change{isReasonRequired ? <span style={{ color: '#C0392B' }}> *</span> : ' (optional)'}
                    </Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={2}
                        value={timeReason}
                        onChange={(e) => setTimeReason(e.target.value)}
                        placeholder={isReasonRequired ? "Required: passenger has not presented at a Reception Desk" : "e.g., Passenger requested a later collection"}
                        isInvalid={isReasonRequired && !timeReason.trim()}
                        style={{ 
                            backgroundColor: usedTheme?.BACKGROUND_COLOR || '#fff', 
                            borderColor: usedTheme?.BORDER_COLOR || '#ccc', 
                            color: usedTheme?.TEXT_COLOR || '#333',
                            maxWidth: '400px'
                        }}
                    />
                </div>
            )}

            {/* Pickup Location */}
            <div className="mb-3">
                <Form.Label style={{ color: usedTheme?.TEXT_COLOR || '#333', fontSize: '13px' }}>Pickup Location</Form.Label>
                <Select
                    value={pickupLocation}
                    onChange={(selectedOption) => setPickupLocation(selectedOption)}
                    options={pickUpLocations?.map(loc => ({
                        value: loc._id,
                        label: loc.title
                    }))}
                    placeholder="Choose Pickup Location..."
                    styles={selectStyles}
                    isClearable
                />
                {job.pickUpLocation?.name && (
                    <p style={{ fontSize: '11px', color: usedTheme?.TEXT_COLOR || '#333', opacity: 0.5, marginTop: '4px', marginBottom: 0 }}>
                        Current: {job.pickUpLocation.name}
                    </p>
                )}
                <p style={{ fontSize: '11px', color: Colors.PRM_LIGHT_BLUE, marginTop: '4px', marginBottom: 0, fontStyle: 'italic' }}>
                    Note: Changing the pickup location will also update the passenger's current location.
                </p>
            </div>

            {/* Drop Off Location */}
            <div className="mb-3">
                <Form.Label style={{ color: usedTheme?.TEXT_COLOR || '#333', fontSize: '13px' }}>Drop Off Location</Form.Label>
                <Select
                    value={dropOffLocation}
                    onChange={(selectedOption) => setDropOffLocation(selectedOption)}
                    options={dropOffLocations?.map(loc => ({
                        value: loc._id,
                        label: loc.title
                    }))}
                    placeholder="Choose Drop Off Location..."
                    styles={selectStyles}
                    isClearable
                />
                {job.dropOffLocation?.name && (
                    <p style={{ fontSize: '11px', color: usedTheme?.TEXT_COLOR || '#333', opacity: 0.5, marginTop: '4px', marginBottom: 0 }}>
                        Current: {job.dropOffLocation.name}
                    </p>
                )}
            </div>

            {/* Assistance Requirements */}
            <div className="mb-3">
                <Form.Label style={{ color: usedTheme?.TEXT_COLOR || '#333', fontSize: '13px' }}>Assistance Requirements</Form.Label>
                <AssistanceSelector
                    setAssistance={setAssistance}
                    defaultValue={assistance}
                    value={assistance}
                />
            </div>

            {/* Notes */}
            <div className="mb-3">
                <BookingNotesSuggestions
                    setBookingNotes={setNotes}
                    bookingNotes={notes}
                />
            </div>

            {/* Update Button */}
            <Button
                variant="outline-primary"
                onClick={handleUpdate}
                disabled={updateStatus === 'loading' || isSavingTime || (isReasonRequired && !timeReason.trim())}
                style={{ minWidth: '160px' }}
            >
                {updateStatus === 'loading' || isSavingTime ? <CustomSpinner variant="light" size={"small"} /> : `Update ${taskTypeLabel}`}
            </Button>

            {/* Error Message */}
            {errorMessage && (
                <p style={{ color: 'red', fontSize: '12px', marginTop: '8px', marginBottom: 0 }}>
                    {errorMessage}
                </p>
            )}
        </div>
    );
};

export default EditAltPickupDetails;
